<template>
  <section class="patch-notes">
    <Banner v-bind="ModulePatchNoteListData"></Banner>
    <section class="patch-notes-wrapper">
      <div v-for="article in articleList" :key="article.title" class="patch-note">
        <div v-if="article.firstSeen" class="patch-note-anchor" />
        <div class="patch-note-content">
          <div v-if="article.firstSeen" class="patch-note-content-date">{{ formatDate(article.date) }}</div>
          <div class="patch-note-content-title" v-html="formatTitle(article.title)"></div>
          <div class="patch-note-content-text" v-html="article.content_html"></div>
        </div>
      </div>
      <div :class="['patch-note', 'load-more', hideLodeMore ? 'hide-load-more' : '']" @click="loadMore">
        {{ formatTransify('UGC_OFFICIAL_WEBSITE_LOAD_MORE') }}
      </div>
      <div v-if="isLoading" class="loading-status" />
    </section>
  </section>
</template>
<script lang="ts" setup>
import { type Ref } from 'vue'
import { formatTime } from '~/utils'
useRouteCache((route) => {
  route.setCacheable().setMaxAge(300).addTags(['patch-notes-index-page-cache-tag'])
})
const { formatTransify } = useStore()
const { fetchPageData, filterModule } = usePageStore()
await fetchPageData('/patch-notes/')
const ModulePatchNoteListData = filterModule('ModulePatchnoteList')

const { fetchArticleList, fetchArticleDetail } = useArticleStore()
const hideLodeMore = ref(true)
const isLoading = ref(false)
const articleList: Ref<any[]> = ref([])
const pagination = ref({ page: 1, size: 10, count: 0 })

const markFirstOccurrence = (items: any[]) => {
  const seenMonths = new Set()
  items.forEach((item: any) => {
    const month = formatTime(item.date).substring(3)
    if (!seenMonths.has(month)) {
      item.firstSeen = true
      seenMonths.add(month)
    } else {
      item.firstSeen = false
    }
  })
}

const getPatchNoteList = async (isRefresh: boolean = false) => {
  isLoading.value = true
  hideLodeMore.value = true
  const result = await fetchArticleList(
    1,
    pagination.value.page,
    pagination.value.size,
    'category',
    ModulePatchNoteListData.category_id
  )
  const promises = result.data.map(async (article: any) => await fetchArticleDetail(article.id))
  const articles = await Promise.all(promises)
  if (isRefresh) {
    articleList.value = [...articles]
  } else {
    articleList.value = [...articleList.value, ...articles]
  }
  markFirstOccurrence(articleList.value)
  pagination.value.count = result.count
  hideLodeMore.value = pagination.value.count - articleList.value.length <= 0
  isLoading.value = false
}

const loadMore = async () => {
  pagination.value.page++
  await getPatchNoteList()
}

const formatTitle = (title: string) => {
  return title.replace('{style}', '<span style="color: #FFBA00">').replace('{style}', '</span>')
}

const formatDate = (date: number) => {
  return formatTime(date).substring(3).replace('/', ' / ')
}
getPatchNoteList(true)
</script>
<style lang="scss" scoped>
.patch-notes-wrapper {
  width: 100%;
  margin-top: 80px;
  display: flex;
  min-height: calc(100vh - 524px);
  align-items: center;
  flex-direction: column;
  background-image: url('@/assets/images/tutorial_texture.jpg');
  background-repeat: repeat-y;
  background-position: 0 0;
  background-size: 100% auto;
  padding: 60px 0;
  @media not all and (min-width: 1024px) {
    & {
      background-image: url('@/assets/images/patch-note-mobile.jpg');
      margin-top: 120px;
      min-height: calc(100vh - 344px);
    }
  }
}
.patch-note {
  position: relative;
  width: 1440px;
  display: flex;
  border-left: 3px solid var(--theme-bg-dark);
  &-anchor {
    position: absolute;
    top: 14.5px;
    left: -6px;
    width: 10px;
    height: 10px;
    transform: rotate(45deg);
    background-color: var(--theme-white);
  }
  &::before {
    content: '';
    display: inline-block;
    width: 1px;
    height: 100%;
    background-color: var(--theme-bg-dark);
  }
  &-content {
    flex: 1;
    padding-left: 60px;
    padding-bottom: 20px;
    &-date {
      width: 152px;
      height: 38px;
      background-image: url('@/assets/images/date-bg.svg');
      background-size: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 26px;
      line-height: 34px;
      font-weight: 400;
      color: #c8ccd3;
      //margin-bottom: 14px;
      margin-left: -5px;
    }
    &-title {
      font-size: 42px;
      line-height: 58px;
      color: #ffffff;
      margin: 0 auto;
      &:deep(&-version) {
        font-size: 42px;
        line-height: 58px;
        color: var(--theme-primary);
      }
    }
    &-version {
      font-size: 42px;
      line-height: 58px;
      color: var(--theme-primary);
    }
    &-text {
      //width: 1070px;
      width: 100%;
      font-size: 1rem;

      &:deep(h1) {
        font-weight: 700;
        font-size: 32px;
        line-height: 48px;
        color: #fff;
      }
      &:deep(h2) {
        font-weight: 500;
        font-size: 24px;
        line-height: 34px;
        color: #fff;
      }
      &:deep(h3) {
        font-weight: 500;
        font-size: 22px;
        line-height: 30px;
        color: #fff;
      }
      &:deep(h4) {
        font-weight: 500;
        font-size: 20px;
        line-height: 28px;
        color: #fff;
      }
      &:deep(p) {
        font-weight: 400;
        font-size: 16px;
        color: #c8ccd3;
        margin: 16px 0;
      }
      &:deep(img) {
        max-width: 100%;
        height: auto;
        object-fit: contain;
      }
      &:deep(pre code) {
        display: block;
        padding: 24px;
        width: 100%;
        line-height: 1.5;
        font-size: 0.9em;
        color: #a6accd;
        transition: color 0.5s;
        background-color: #323232;
        border-radius: 10px;
        margin: 20px 0;
        white-space: pre;
        span {
          color: #ffba00;
        }
      }
      &:deep(code) {
        margin: 0;
        border-radius: 4px;
        padding: 0.25rem 0.5rem;
        font-family: 'GFF Latin', 'Helvetica Neue', 'Helvetica', 'Arial', 'Microsoft YaHei', '微软雅黑', 'sans-serif';
        font-size: 0.85em;
        background-color: #5c5f6d;
        overflow: auto;
      }
      &:deep(a) {
        text-decoration: underline;
      }
      &:deep(ol li) {
        list-style-type: decimal;
      }
      &:deep(ul li) {
        list-style-type: disc;
      }
      &:deep(ol),
      &:deep(ul) {
        margin: 1em 0;
        line-height: 1.7;
        padding-left: 1em;
      }
      /* Table */
      &:deep(table) {
        border-collapse: collapse;
        width: 100%;
        border: 1px solid #7a7e85;
        margin-bottom: 20px;
      }
      //&:deep(table thead tr) {
      //background-color: var(--table-thead-color);
      //}

      &:deep(table tr th) {
        font-weight: bold;
        border-left: 1px solid #7a7e85;
        border-right: 1px solid #7a7e85;
        text-align: left;
        margin: 0;
        padding: 6px 10px;
        height: 40px;
      }

      &:deep(table tbody tr) {
        border-top: 1px solid #7a7e85;
        margin: 0;
        padding: 0;
      }

      //table tbody tr:nth-child(2n) {
      //background-color: var(--table-bg-darker-color);
      //}

      //table tbody tr:nth-child(2n + 1) {
      //background-color: var(--table-bg-color);
      //}

      &:deep(table tr td) {
        border-left: 1px solid #7a7e85;
        border-right: 1px solid #7a7e85;
        text-align: left;
        margin: 0;
        height: 40px;
        padding: 6px 10px;
      }

      &:deep(table tr th:first-child, table tr td:first-child) {
        border-left-width: 0;
      }

      &:deep(table tr th:last-child, table tr td:last-child) {
        border-right-width: 0;
      }
      //&:deep(table) {
      //  text-indent: 0;
      //  border-color: inherit;
      //  border-collapse: collapse;
      //  width: 100%;
      //}
      //&:deep(table tr th) {
      //  color: #fff;
      //  font-size: 16px;
      //  min-height: 40px;
      //  padding: 6px 10px;
      //}
      //&:deep(table tr td) {
      //  color: #c8ccd3;
      //  min-height: 40px;
      //  padding: 6px 10px;
      //}
      &:deep(blockquote) {
        position: relative;
        padding: 10px 20px;
        background-color: #363a41;
      }
      &:deep(blockquote):before {
        position: absolute;
        top: 0;
        left: 0;
        width: 2px;
        height: 100%;
        background-color: #ffba00;
      }
      &:deep(video) {
        width: 100%;
        margin: 16px auto;
      }
      &:deep(iframe) {
        width: 700px;
        height: 394px;
        display: block;
        margin: 16px 0;
        position: relative;
      }
      &:deep(hr) {
        width: 100%;
        border-top: 1px solid #2b2d32;
      }
    }
  }
  &.load-more {
    display: flex;
    justify-content: center;
    align-items: center;
    text-transform: uppercase;
    color: #c8ccd3;
    cursor: pointer;
    font-size: 24px;
    &::after {
      content: '';
      width: 0;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 7px solid #c8ccd3;
      margin-left: 5px;
    }
    &:hover {
      color: var(--theme-white);
      &::after {
        border-top: 7px solid var(--theme-white);
      }
    }
    &.hide-load-more {
      opacity: 0;
    }
  }
  .loading-status {
    background-image: url('@/assets/images/loading.svg');
    width: 44px;
    height: 44px;
    margin: 0 auto 27px;
  }
}
@media not all and (min-width: 1024px) {
  .patch-note {
    width: 686px;
    &-content {
      width: 100%;
      padding-left: 28px;
      padding-bottom: 28px;
      &-date {
        color: var(--theme-white);
      }
      &-text {
        padding: 14px 0 30px;
        font-size: 24px;
        width: 100%;
        &:deep(h1) {
          font-size: 36px;
          line-height: 50px;
        }
        &:deep(h2) {
          font-size: 34px;
          line-height: 42px;
        }
        &:deep(h3) {
          font-size: 32px;
          line-height: 38px;
        }
        &:deep(h4) {
          font-size: 28px;
          line-height: 32px;
        }
        &:deep(p) {
          font-size: 26px;
          line-height: 32px;
        }

        &:deep(pre code) {
          display: block;
          padding: 24px;
          width: 100%;
          line-height: 1.5;
          font-size: 0.9em;
          color: #a6accd;
          transition: color 0.5s;
          background-color: #323232;
          border-radius: 10px;
          margin: 20px 0;
          white-space: pre;
          span {
            color: #ffba00;
          }
        }
        &:deep(code) {
          font-size: 24px;
        }

        &:deep(iframe) {
          width: 680px;
          height: 382px;
        }
      }
    }
  }
  @media screen and (orientation: landscape) {
    .patch-note {
      width: 85%;
    }
  }
}
</style>
